// Global singleton audio player for one-shot TTS playback.
//
// Why this exists: previously every component that played read-aloud
// audio (ReadAloudButton, TodayBanner, PlayAllTodayButton, VoiceSettings,
// the daily devotional card, etc.) called `new Audio(url)` and `.play()`
// directly. That works fine if the user only ever taps one button at a
// time, but if they tap "Read this verse" while the Daily Devotional is
// still reading, both audio elements play simultaneously — the
// "talking over each other" bug.
//
// This module owns ONE shared <audio> element. Any call to playOnce()
// stops whatever else is currently playing before starting the new
// clip. That guarantees only one TTS voice is ever audible.
//
// Returns the underlying HTMLAudioElement so callers can still attach
// `onended` / `onpause` listeners (e.g. to update their UI state).

let _el = null;
const _listeners = new Set();

function _ensure() {
  if (_el) return _el;
  const el = new Audio();
  el.preload = "auto";
  try { window.__hwAudioEl = el; } catch (_) {}
  // Whenever this singleton fires native events, fan them out to any
  // subscriber so multiple components can sync their UI to the same
  // playback.
  ["play", "pause", "ended", "error"].forEach((evt) => {
    el.addEventListener(evt, () => {
      _listeners.forEach((cb) => {
        try {
          cb(evt, el);
        } catch (_) {}
      });
    });
  });
  _el = el;
  return el;
}

// Stop any current playback. Idempotent and safe to call before
// starting a new clip from any component.
export function stopOnce() {
  if (!_el) return;
  try {
    _el.pause();
    _el.currentTime = 0;
  } catch (_) {}
  try {
    if (_el.src && _el.src.startsWith("blob:")) URL.revokeObjectURL(_el.src);
  } catch (_) {}
  _el.removeAttribute("src");
  try {
    _el.load();
  } catch (_) {}
}

// Start playing a URL. Returns a promise that resolves when playback
// has actually started, or rejects with the play error.
export async function playOnce(url) {
  const el = _ensure();
  // Stop anything else first — the whole point of this module.
  stopOnce();
  el.src = url;
  el.currentTime = 0;
  return el.play();
}

// Subscribe to native playback events on the singleton. Returns an
// unsubscribe fn. Useful when a component needs to flip its
// "isPlaying" UI state in response to clips started by other
// components.
export function onPlaybackEvent(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

// Expose the singleton for components that need direct control
// (e.g. seeking, volume). Always prefer playOnce / stopOnce when
// possible.
export function getSingletonAudio() {
  return _ensure();
}
